import type { ApiError, MeResponse, PostDetail, PostListItem, PostVersionItem, PostsResponse, ProfileResponse, UserPublicProfileResponse } from "./types";

async function asJson<T>(r: Response): Promise<T> {
  const t = await r.text();
  try {
    return JSON.parse(t) as T;
  } catch {
    return { error: `bad_json: ${t.slice(0, 200)}` } as unknown as T;
  }
}

function makeIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `idem_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function pickFirst<T = unknown>(obj: any, keys: string[], fallback?: T): T | undefined {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k] as T;
  }
  return fallback;
}

function normalizePostItem(p: any): PostListItem {
  return {
    ...p,
    authorId: pickFirst<string>(p, ["authorId", "authorid", "author_id"]) || "",
    authorUsername: pickFirst<string>(p, ["authorUsername", "authorusername"]) || "",
    authorAvatar: pickFirst<string>(p, ["authorAvatar", "authoravatar"]) || "",
    authorNameColor: pickFirst<string>(p, ["authorNameColor", "authornamecolor"]) || "",
    authorScore: Number(pickFirst<number | string>(p, ["authorScore", "authorscore"], 0) || 0),
  } as PostListItem;
}

function normalizePostDetail(p: any): PostDetail {
  const rawComments = Array.isArray(p?.comments) ? p.comments : [];
  const comments = rawComments.map((c: any) => ({
    ...c,
    createdAt: pickFirst(c, ["createdAt", "createdat", "created_at"]),
    updatedAt: pickFirst(c, ["updatedAt", "updatedat", "updated_at"]),
    parentId: pickFirst(c, ["parentId", "parentid", "parent_id"]),
    userId: pickFirst(c, ["userId", "userid", "user_id"]),
    canEdit: Boolean(pickFirst(c, ["canEdit", "canedit"], false)),
  }));
  return {
    ...p,
    authorId: pickFirst<string>(p, ["authorId", "authorid", "author_id"]) || "",
    authorUsername: pickFirst<string>(p, ["authorUsername", "authorusername"]) || "",
    authorAvatar: pickFirst<string>(p, ["authorAvatar", "authoravatar"]) || "",
    authorNameColor: pickFirst<string>(p, ["authorNameColor", "authornamecolor"]) || "",
    authorScore: Number(pickFirst<number | string>(p, ["authorScore", "authorscore"], 0) || 0),
    comments,
  } as PostDetail;
}

type EtagCacheEntry<T> = { etag: string; value: T; ts: number };

const postDetailEtagCache = new Map<string, EtagCacheEntry<PostDetail>>();
const postBatchEtagCache = new Map<string, EtagCacheEntry<{ items: PostDetail[] }>>();

function cacheSet<T>(map: Map<string, EtagCacheEntry<T>>, key: string, etag: string, value: T, maxSize = 400) {
  map.set(key, { etag, value, ts: Date.now() });
  if (map.size <= maxSize) return;
  let oldestKey = "";
  let oldestTs = Number.POSITIVE_INFINITY;
  for (const [k, v] of map.entries()) {
    if (v.ts < oldestTs) {
      oldestTs = v.ts;
      oldestKey = k;
    }
  }
  if (oldestKey) map.delete(oldestKey);
}

export const api = {
  me: async (): Promise<MeResponse> => {
    const r = await fetch("/api/me", { credentials: "include" });
    return asJson<MeResponse>(r);
  },
  logout: async (): Promise<void> => {
    await fetch("/api/logout", { method: "POST", credentials: "include" });
  },
  list: async (): Promise<{ items: PostListItem[] } | ApiError> => {
    const r = await fetch("/api/posts", { credentials: "include" });
    const data: any = await asJson(r);
    if ("error" in data) return data;
    return { ...data, items: Array.isArray(data.items) ? data.items.map(normalizePostItem) : [] };
  },
  listPaged: async (params: { sort: "latest" | "top"; cursor?: string | null; limit?: number; q?: string }): Promise<PostsResponse> => {
    const sp = new URLSearchParams();
    sp.set("sort", params.sort);
    if (params.cursor) sp.set("cursor", params.cursor);
    if (params.limit) sp.set("limit", String(params.limit));
    if (params.q && params.q.trim()) sp.set("q", params.q.trim());
    const r = await fetch(`/api/posts?${sp.toString()}`, { credentials: "include" });
    const data: any = await asJson(r);
    if ("error" in data) return data;
    return { ...data, items: Array.isArray(data.items) ? data.items.map(normalizePostItem) : [] } as PostsResponse;
  },
  myPostsPaged: async (params: { sort: "latest" | "top"; cursor?: string | null; limit?: number }): Promise<PostsResponse> => {
    const sp = new URLSearchParams();
    sp.set("sort", params.sort);
    if (params.cursor) sp.set("cursor", params.cursor);
    if (params.limit) sp.set("limit", String(params.limit));
    const r = await fetch(`/api/my-posts?${sp.toString()}`, { credentials: "include" });
    const data: any = await asJson(r);
    if ("error" in data) return data;
    return { ...data, items: Array.isArray(data.items) ? data.items.map(normalizePostItem) : [] } as PostsResponse;
  },
  userProfile: async (id: string): Promise<UserPublicProfileResponse> => {
    const r = await fetch(`/api/user/${encodeURIComponent(id)}`, { credentials: "include" });
    return asJson(r);
  },
  userPostsPaged: async (id: string, params: { sort: "latest" | "top"; cursor?: string | null; limit?: number }): Promise<PostsResponse> => {
    const sp = new URLSearchParams();
    sp.set("sort", params.sort);
    if (params.cursor) sp.set("cursor", params.cursor);
    if (params.limit) sp.set("limit", String(params.limit));
    const r = await fetch(`/api/user/${encodeURIComponent(id)}/posts?${sp.toString()}`, { credentials: "include" });
    const data: any = await asJson(r);
    if ("error" in data) return data;
    return { ...data, items: Array.isArray(data.items) ? data.items.map(normalizePostItem) : [] } as PostsResponse;
  },
  favorites: async (): Promise<{ items: PostListItem[] } | ApiError> => {
    const r = await fetch("/api/favorites", { credentials: "include" });
    const data: any = await asJson(r);
    if ("error" in data) return data;
    return { ...data, items: Array.isArray(data.items) ? data.items.map(normalizePostItem) : [] };
  },
  setFavorite: async (postId: string, value: boolean): Promise<{ ok: true } | ApiError> => {
    const r = await fetch("/api/favorites", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ postId, value }),
    });
    return asJson(r);
  },
  get: async (id: string, configId?: string): Promise<PostDetail | ApiError> => {
    const qs = configId ? `?config=${encodeURIComponent(configId)}` : "";
    const cacheKey = `${String(id || "")}::${String(configId || "")}`;
    const cached = postDetailEtagCache.get(cacheKey);
    const headers: Record<string, string> = {};
    if (cached?.etag) headers["if-none-match"] = cached.etag;
    const r = await fetch(`/api/post/${encodeURIComponent(id)}${qs}`, { credentials: "include", headers });
    if (r.status === 304 && cached) return cached.value;
    const data: any = await asJson(r);
    if ("error" in data) return data;
    const normalized = normalizePostDetail(data);
    const etag = String(r.headers.get("etag") || "").trim();
    if (etag) cacheSet(postDetailEtagCache, cacheKey, etag, normalized);
    return normalized;
  },
  postBatch: async (ids: string[]): Promise<{ items: PostDetail[] } | ApiError> => {
    const uniq = Array.from(new Set((ids || []).map((x) => String(x || "").trim()).filter(Boolean)));
    if (!uniq.length) return { items: [] };
    const cacheKey = uniq.join(",");
    const cached = postBatchEtagCache.get(cacheKey);
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (cached?.etag) headers["if-none-match"] = cached.etag;
    const r = await fetch("/api/post-batch", {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ ids: uniq }),
    });
    if (r.status === 304 && cached) return cached.value;
    const data: any = await asJson(r);
    if ("error" in data) return data;
    const normalized = {
      items: Array.isArray(data.items) ? data.items.map(normalizePostDetail) : [],
    };
    const etag = String(r.headers.get("etag") || "").trim();
    if (etag) cacheSet(postBatchEtagCache, cacheKey, etag, normalized);
    return normalized;
  },
  profile: async (): Promise<ProfileResponse> => {
    const r = await fetch("/api/profile", { credentials: "include" });
    return asJson(r);
  },
  updateProfile: async (payload: { username?: string; avatar?: string; nameColor?: string; topbarColor?: string }): Promise<{ ok: true } | ApiError> => {
    const r = await fetch("/api/profile", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    return asJson(r);
  },
  publish: async (payload: {
    title: string;
    description: string;
    code?: string;
    versionMode?: "per_edition" | "global";
    activeConfigId?: string;
    configs?: Array<{ id: string; title: string; description?: string; code: string }>;
  }): Promise<{ ok: true; id: string } | ApiError> => {
    const idem = makeIdempotencyKey();
    const r = await fetch("/api/post", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", "idempotency-key": idem },
      body: JSON.stringify(payload),
    });
    return asJson(r);
  },
  updatePost: async (
    id: string,
    payload: {
      title: string;
      description: string;
      code?: string;
      configId?: string;
      versionMode?: "per_edition" | "global";
      newVersionMode?: "per_edition" | "global";
      activeConfigId?: string;
      configs?: Array<{ id: string; title: string; description?: string; code: string }>;
    }
  ): Promise<{ ok: true; id: string; version?: number; versionMode?: "per_edition" | "global"; activeConfigId?: string } | ApiError> => {
    const idem = makeIdempotencyKey();
    const r = await fetch(`/api/post/${encodeURIComponent(id)}/edit`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", "idempotency-key": idem },
      body: JSON.stringify(payload),
    });
    return asJson(r);
  },
  deletePost: async (id: string): Promise<{ ok: true; id: string } | ApiError> => {
    const r = await fetch(`/api/post/${encodeURIComponent(id)}/delete`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    return asJson(r);
  },
  postVersions: async (
    id: string,
    opts?: { configId?: string; all?: boolean }
  ): Promise<{ items: PostVersionItem[]; versionMode?: "per_edition" | "global"; configKey?: string } | ApiError> => {
    const sp = new URLSearchParams();
    if (opts?.configId) sp.set("config", opts.configId);
    if (opts?.all) sp.set("all", "1");
    const qs = sp.toString() ? `?${sp.toString()}` : "";
    const r = await fetch(`/api/post/${encodeURIComponent(id)}/versions${qs}`, { credentials: "include" });
    return asJson(r);
  },
  rollbackPostVersion: async (id: string, version: number, configId?: string): Promise<{ ok: true; id: string; version: number } | ApiError> => {
    const r = await fetch(`/api/post/${encodeURIComponent(id)}/rollback`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ version, config: configId || null }),
    });
    return asJson(r);
  },
  vote: async (id: string, value: 1 | -1): Promise<{ ok: true } | ApiError> => {
    const r = await fetch(`/api/post/${encodeURIComponent(id)}/vote`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ value }),
    });
    return asJson(r);
  },
  comment: async (id: string, body: string, parentId?: string): Promise<{ ok: true } | ApiError> => {
    const r = await fetch(`/api/post/${encodeURIComponent(id)}/comment`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ body, parentId: parentId || null }),
    });
    return asJson(r);
  },
  editComment: async (postId: string, commentId: string, body: string): Promise<{ ok: true } | ApiError> => {
    const r = await fetch(`/api/post/${encodeURIComponent(postId)}/comment/${encodeURIComponent(commentId)}/edit`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ body }),
    });
    return asJson(r);
  },
  report: async (id: string, reason: string): Promise<{ ok: true } | ApiError> => {
    const r = await fetch(`/api/post/${encodeURIComponent(id)}/report`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reason }),
    });
    return asJson(r);
  },
  settings: async (): Promise<{
    scoreZeroColor: string;
    scoreZeroAccent: string;
    postCardLightFrom: string;
    postCardLightTo: string;
    discordUrl?: string;
  } | ApiError> => {
    const r = await fetch("/api/settings", { credentials: "include" });
    return asJson(r);
  },
  adminSettings: async (): Promise<{
    scoreZeroColor: string;
    scoreZeroAccent: string;
    postCardLightFrom: string;
    postCardLightTo: string;
  } | ApiError> => {
    const r = await fetch("/api/admin/settings", { credentials: "include" });
    return asJson(r);
  },
  updateAdminSettings: async (payload: {
    scoreZeroColor?: string;
    scoreZeroAccent?: string;
    postCardLightFrom?: string;
    postCardLightTo?: string;
  }): Promise<{ ok: true } | ApiError> => {
    const r = await fetch("/api/admin/settings", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    return asJson(r);
  },
  tokenStatus: async (): Promise<{ hasToken: boolean; createdAt: number | null; lastUsedAt: number | null; name: string } | ApiError> => {
    const r = await fetch("/api/profile/token", { credentials: "include" });
    return asJson(r);
  },
  tokenCreate: async (name: string): Promise<{ ok: true; token: string } | ApiError> => {
    const r = await fetch("/api/profile/token", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name }),
    });
    return asJson(r);
  },
  tokenRevoke: async (): Promise<{ ok: true } | ApiError> => {
    const r = await fetch("/api/profile/token", {
      method: "DELETE",
      credentials: "include",
    });
    return asJson(r);
  },
  connectCreate: async (payload: { client: string }): Promise<{ ok: true; code: string; expiresAt: number } | ApiError> => {
    const r = await fetch("/api/connect/create", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    return asJson(r);
  },
  usersSearch: async (q: string): Promise<{ items: Array<{ id: string; username: string; avatar?: string; nameColor?: string; totalScore?: number }> } | ApiError> => {
    const url = new URL("/api/users/search", location.origin);
    url.searchParams.set("q", q);
    const r = await fetch(url.toString(), { method: "GET", credentials: "include" });
    return asJson(r);
  },
  adminUsers: async (q: string): Promise<{ items: any[] } | ApiError> => {
    const url = new URL("/api/admin/users", location.origin);
    if (q) url.searchParams.set("q", q);
    const r = await fetch(url.toString(), { method: "GET", credentials: "include" });
    return asJson(r);
  },
  adminAnalytics: async (): Promise<{ enabled: boolean; dashboardUrl: string; websiteId: string } | ApiError> => {
    const r = await fetch("/api/admin/analytics", { credentials: "include" });
    return asJson(r);
  },
  adminBackupTestUpload: async (): Promise<{ ok: true; uploaded: { id: string; name: string; webViewLink: string; createdTime: string } } | ApiError> => {
    const r = await fetch("/api/admin/backup-test", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    return asJson(r);
  },
  adminBackupRun: async (force = false): Promise<
    | {
        ok: true;
        skipped: boolean;
        reason?: string;
        latest?: any;
        uploaded?: { id: string; name: string; webViewLink: string; createdTime: string; hash: string; bytes: number };
        retention?: { kept: number; deleted: number; limit: number };
      }
    | ApiError
  > => {
    const r = await fetch("/api/admin/backup-run", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ force }),
    });
    return asJson(r);
  },
  adminRecomputeAllPostRanks: async (): Promise<
    | {
        ok: true;
        scannedPosts: number;
        updatedPosts: number;
        updatedConfigs: number;
        failedPosts: number;
        failed?: Array<{ postId: string; error: string }>;
      }
    | ApiError
  > => {
    const r = await fetch("/api/admin/recompute-ranks", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    return asJson(r);
  },
  adminBanUser: async (id: string, payload: { banned: boolean; reason?: string }): Promise<{ ok: true } | ApiError> => {
    const r = await fetch(`/api/admin/user/${encodeURIComponent(id)}/ban`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    return asJson(r);
  },
  adminDeleteUser: async (id: string): Promise<{ ok: true } | ApiError> => {
    const r = await fetch(`/api/admin/user/${encodeURIComponent(id)}/delete`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    return asJson(r);
  },
};
